import type { ProjectStatus } from './project.model';

/**
 * Portfolio reporting (spec: project-portfolio-reporting.md, Story 1/4).
 *
 * Read-only aggregation over `projects`/`project_activities`/`time_entries`/
 * `cost_rates` — no persisted state of its own.
 */

/** Traffic-light risk level of a project. `grey` = not `active`, or no budget configured. */
export type PortfolioRiskLevel = 'green' | 'yellow' | 'red' | 'grey';

export interface PortfolioKpis {
  /** Projects with status `active`. */
  activeProjects: number;
  /** Projects with status `draft` + `on_hold` + `completed`. */
  inactiveProjects: number;
  /** Projects with `riskLevel` `yellow` or `red`. */
  atRiskCount: number;
  /** Sum of `actualHours` over ALL projects. */
  totalActualHours: number;
  /** Sum of `budgetHours` over all projects that have one; `null` if no project has a budget at all. */
  totalBudgetHours: number | null;
  /** Sum of the forecasted revenue over active projects with a resolvable cost rate; `null` if none has one. */
  forecastRevenue: number | null;
}

export interface PortfolioProjectRow {
  id: string;
  number: string;
  name: string;
  customer: string | null;
  status: ProjectStatus;
  /** Sum of assigned activity budgets (hours); `null` when the project has none. */
  budgetHours: number | null;
  /** Sum of ALL booked minutes on the project, in hours (regardless of `billable`). */
  actualHours: number;
  /** Extrapolation of `actualHours` over the project period, or `actualHours` unchanged when no period is set. */
  forecastHours: number;
  /** `false` when `startDate`/`endDate` is missing — `forecastHours` is then not an extrapolation. */
  hasForecastPeriod: boolean;
  /** `forecastHours / budgetHours * 100`; `null` when `budgetHours` is `null`. */
  utilizationPct: number | null;
  riskLevel: PortfolioRiskLevel;
  /** Human-readable (German) reasons behind `riskLevel`, most significant first. */
  riskReasons: string[];
}

export interface PortfolioReport {
  kpis: PortfolioKpis;
  projects: PortfolioProjectRow[];
}
